import { useState } from 'react';
import { JobFiltersInput, DateRange } from '@/hooks/graphql/useAdminJobsGraphQL';

interface JobQueueFiltersProps {
  filters: JobFiltersInput;
  onFiltersChange: (filters: JobFiltersInput) => void;
}

/**
 * Filter controls for Job Queue Inspector
 * Feature: 073-job-queue-inspector
 *
 * Provides filtering by:
 * - State category (pending/active/completed/failed)
 * - Date range (last day, 7 days, 30 days, 90 days)
 * - Queue name
 * - Search (job ID, queue name, data/output fields, or #globalId)
 */
export function JobQueueFilters({ filters, onFiltersChange }: JobQueueFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  const handleCategoryChange = (category: string) => {
    const newFilters = { ...filters };
    delete newFilters.state;
    delete newFilters.states;
    delete newFilters.stateCategory;

    if (category && category !== 'all') {
      newFilters.stateCategory = category as 'pending' | 'active' | 'completed' | 'failed';
    }

    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (dateRange: string) => {
    const newFilters = { ...filters };
    if (dateRange && dateRange !== 'all') {
      newFilters.dateRange = dateRange as DateRange;
    } else {
      delete newFilters.dateRange;
    }
    onFiltersChange(newFilters);
  };

  const handleQueueNameChange = (name: string) => {
    const newFilters = { ...filters };
    if (name) {
      newFilters.name = name;
    } else {
      delete newFilters.name;
    }
    onFiltersChange(newFilters);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newFilters = { ...filters };
    if (searchInput.trim()) {
      newFilters.search = searchInput.trim();
    } else {
      delete newFilters.search;
    }
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    onFiltersChange({});
  };

  const hasActiveFilters =
    filters.stateCategory || filters.name || filters.search || filters.state || filters.states || filters.dateRange;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Filters Row - Status and Date Range side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* State Category Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'active', 'completed', 'failed'].map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  (category === 'all' && !filters.stateCategory) ||
                  filters.stateCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All time' },
              { value: DateRange.LAST_DAY, label: 'Last day' },
              { value: DateRange.LAST_7_DAYS, label: 'Last 7d' },
              { value: DateRange.LAST_30_DAYS, label: 'Last 30d' },
              { value: DateRange.LAST_90_DAYS, label: 'Last 90d' },
            ].map((range) => (
              <button
                key={range.value}
                onClick={() => handleDateRangeChange(range.value)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  (range.value === 'all' && !filters.dateRange) ||
                  filters.dateRange === range.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Queue Name Filter */}
      <div>
        <label htmlFor="queue-name" className="block text-xs font-medium text-gray-700 mb-1">
          Queue Name
        </label>
        <select
          id="queue-name"
          value={filters.name || ''}
          onChange={(e) => handleQueueNameChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All queues</option>
          <option value="ai-feedback">ai-feedback</option>
          <option value="email">email</option>
          <option value="cleanup">cleanup</option>
        </select>
      </div>

      {/* Search Filter */}
      <div>
        <label htmlFor="job-search" className="block text-xs font-medium text-gray-700 mb-1">
          Search
        </label>
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <input
            id="job-search"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Job ID, queue, data, or #globalId (e.g., #cs24)..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-1">
          Search across job IDs, queue names, input data, and output. Use #globalId (e.g., #cs24, #ph6l) to find jobs by entity.
        </p>
        <p className="text-xs text-amber-600 mt-1">
          ⚠️ Searches including data/output fields may be slow on large job tables. Consider combining with other filters.
        </p>
      </div>
    </div>
  );
}
